import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { startOfDay, differenceInDays } from 'date-fns';
import { toast } from '@/hooks/ui/use-toast';

// Types for audit logging
export type ShiftEditType =
  | 'employee_changed'
  | 'times_changed'
  | 'breaks_changed'
  | 'created_retroactively'
  | 'moved_to_past'
  | 'soft_deleted'
  | 'notes_changed'
  | 'title_changed';

export interface ShiftEditAuditRecord {
  id: string;
  shift_id: string;
  business_id: string;
  edit_type: ShiftEditType;
  old_value: any;
  new_value: any;
  edited_by_user_id: string;
  edited_at: string;
  change_reason?: string;
}

interface HistoricalEditContextValue {
  // Session-based toggle (resets on page reload)
  isHistoricalEditEnabled: boolean;
  enableHistoricalEdit: () => void;
  disableHistoricalEdit: () => void;

  // Grace period from business settings
  gracePeriodDays: number;
  setGracePeriodDays: (days: number) => void;

  // Check if a shift is locked (protected from editing)
  isShiftLocked: (shiftDate: Date, isInHistory?: boolean) => boolean;

  // Check if a shift is within grace period
  isWithinGracePeriod: (shiftDate: Date) => boolean;

  // Session tracking for immediate UI feedback
  sessionEditedShiftIds: Set<string>;
  markShiftAsEdited: (shiftId: string) => void;

  // Audit logging
  recordHistoricalEdit: (
    shiftId: string,
    businessId: string,
    editType: ShiftEditType,
    oldValue: any,
    newValue: any
  ) => Promise<void>;

  // Fetch audit history for a shift
  fetchShiftAuditHistory: (shiftId: string) => Promise<ShiftEditAuditRecord[]>;

  // Check if shift was historically edited
  wasShiftHistoricallyEdited: (shiftId: string) => boolean;
}

const HistoricalEditContext = createContext<HistoricalEditContextValue | undefined>(undefined);

interface HistoricalEditProviderProps {
  children: React.ReactNode;
  initialGracePeriodDays?: number;
}

export const HistoricalEditProvider: React.FC<HistoricalEditProviderProps> = ({
  children,
  initialGracePeriodDays = 3
}) => {
  const { user } = useAuth();

  // Session-only state - resets on page reload
  const [isHistoricalEditEnabled, setIsHistoricalEditEnabled] = useState(false);
  const [gracePeriodDays, setGracePeriodDays] = useState(initialGracePeriodDays);
  const [sessionEditedShiftIds, setSessionEditedShiftIds] = useState<Set<string>>(new Set());

  // Track shifts that were historically edited (fetched from DB)
  const [historicallyEditedShiftIds, setHistoricallyEditedShiftIds] = useState<Set<string>>(new Set());

  const enableHistoricalEdit = useCallback(() => {
    setIsHistoricalEditEnabled(true);
    toast.success('Historical Edit Mode Enabled', {
      description: 'You can now edit past shifts. Changes will be audited.'
    });
  }, []);

  const disableHistoricalEdit = useCallback(() => {
    setIsHistoricalEditEnabled(false);
    toast('Historical Edit Mode Disabled', {
      description: 'Past shifts are now protected.'
    });
  }, []);

  // Check if a shift date is within the grace period
  const isWithinGracePeriod = useCallback((shiftDate: Date): boolean => {
    const today = startOfDay(new Date());
    const shiftDay = startOfDay(shiftDate);
    const daysDifference = differenceInDays(today, shiftDay);

    // Within grace period if the shift is 0 to gracePeriodDays days in the past
    return daysDifference >= 0 && daysDifference <= gracePeriodDays;
  }, [gracePeriodDays]);

  // Check if a shift is locked (protected from editing)
  const isShiftLocked = useCallback((shiftDate: Date, isInHistory: boolean = false): boolean => {
    // If historical edit mode is enabled, nothing is locked
    if (isHistoricalEditEnabled) {
      return false;
    }

    const today = startOfDay(new Date());
    const shiftDay = startOfDay(shiftDate);

    // Future shifts are never locked
    if (shiftDay > today) {
      return false;
    }

    // Today's shifts are not locked
    if (shiftDay.getTime() === today.getTime()) {
      return false;
    }

    // Shifts in the historical table are always locked (unless in edit mode)
    if (isInHistory) {
      return true;
    }

    // Check grace period for past shifts still in main table
    if (isWithinGracePeriod(shiftDate)) {
      return false;
    }

    // All other past shifts are locked
    return true;
  }, [isHistoricalEditEnabled, isWithinGracePeriod]);

  // Mark a shift as edited in the current session
  const markShiftAsEdited = useCallback((shiftId: string) => {
    setSessionEditedShiftIds(prev => new Set([...prev, shiftId]));
  }, []);

  // Record a historical edit in the audit log
  const recordHistoricalEdit = useCallback(async (
    shiftId: string,
    businessId: string,
    editType: ShiftEditType,
    oldValue: any,
    newValue: any
  ): Promise<void> => {
    if (!user?.id) {
      console.error('Cannot record historical edit: user not authenticated');
      return;
    }

    try {
      const { error } = await supabase
        .from('shift_edit_audit')
        .insert({
          shift_id: shiftId,
          business_id: businessId,
          edit_type: editType,
          old_value: oldValue,
          new_value: newValue,
          edited_by_user_id: user.id,
          edited_at: new Date().toISOString()
        });

      if (error) {
        console.error('Failed to record historical edit:', error);
        throw error;
      }

      // Mark this shift as edited in the session
      markShiftAsEdited(shiftId);

      // Also update the shift's historical edit flag
      // Try to update in shifts table first, then shifts_history
      const { data: shiftData, error: updateError } = await supabase
        .from('shifts')
        .update({
          was_historically_edited: true
        })
        .eq('id', shiftId)
        .select('id')
        .maybeSingle();

      if (!shiftData) {
        // Shift not in shifts table, try shifts_history table
        await supabase
          .from('shifts_history')
          .update({
            was_historically_edited: true
          })
          .eq('id', shiftId);
      }

      // Increment the historical edit count using raw SQL
      await supabase.rpc('increment_historical_edit_count', { shift_id_param: shiftId });

    } catch (error) {
      console.error('Failed to record historical edit:', error);
    }
  }, [user?.id, markShiftAsEdited]);

  // Fetch audit history for a specific shift
  const fetchShiftAuditHistory = useCallback(async (shiftId: string): Promise<ShiftEditAuditRecord[]> => {
    try {
      const { data, error } = await supabase
        .from('shift_edit_audit')
        .select('*')
        .eq('shift_id', shiftId)
        .order('edited_at', { ascending: false });

      if (error) {
        console.error('Failed to fetch shift audit history:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Failed to fetch shift audit history:', error);
      return [];
    }
  }, []);

  // Check if a shift was historically edited
  const wasShiftHistoricallyEdited = useCallback((shiftId: string): boolean => {
    return sessionEditedShiftIds.has(shiftId) || historicallyEditedShiftIds.has(shiftId);
  }, [sessionEditedShiftIds, historicallyEditedShiftIds]);

  const value = useMemo<HistoricalEditContextValue>(() => ({
    isHistoricalEditEnabled,
    enableHistoricalEdit,
    disableHistoricalEdit,
    gracePeriodDays,
    setGracePeriodDays,
    isShiftLocked,
    isWithinGracePeriod,
    sessionEditedShiftIds,
    markShiftAsEdited,
    recordHistoricalEdit,
    fetchShiftAuditHistory,
    wasShiftHistoricallyEdited
  }), [
    isHistoricalEditEnabled,
    enableHistoricalEdit,
    disableHistoricalEdit,
    gracePeriodDays,
    setGracePeriodDays,
    isShiftLocked,
    isWithinGracePeriod,
    sessionEditedShiftIds,
    markShiftAsEdited,
    recordHistoricalEdit,
    fetchShiftAuditHistory,
    wasShiftHistoricallyEdited
  ]);

  return (
    <HistoricalEditContext.Provider value={value}>
      {children}
    </HistoricalEditContext.Provider>
  );
};

export const useHistoricalEdit = (): HistoricalEditContextValue => {
  const context = useContext(HistoricalEditContext);
  if (!context) {
    throw new Error('useHistoricalEdit must be used within a HistoricalEditProvider');
  }
  return context;
};

export default HistoricalEditContext;
